// frontend/src/components/PagarFaturaModal/PagarFaturaModal.jsx
import React, { useState, useEffect, useMemo } from 'react';
import {
    Modal, Form, Select, Button, Alert, Row, Col, InputNumber, DatePicker,
    Typography, Descriptions, Checkbox, Divider, Input, message,
} from 'antd';
import { pagarFatura, getCotacaoUsdBrl } from '../../services/cardService';
import { getContas } from '../../services/accountService';
import { getPortadores, getProgramasDoPortador, createPontosCartao } from '../../services/milhasService';
import dayjs from 'dayjs';
import { getErrorMessage } from '../../utils/errorHandler';
import {
    estimarPontosFatura,
    observacaoPontosFatura,
    ensureObservacaoComFaturaId,
    somarPagamentosParciaisFatura,
    valorBasePontosFatura,
    DIAS_CREDITO_PONTOS_APOS_VENCIMENTO,
} from '../../utils/estimativaPontosCartao';
import { useAuth } from '../../contexts/AuthContext';
import ModuloUpsellModal from '../ModuloUpsellModal/ModuloUpsellModal';
import MobileLaunchSheet from '../MobileLaunchSheet/MobileLaunchSheet';

const { Option } = Select;
const { Text } = Typography;

// Pontos costumam entrar poucos dias após o vencimento da fatura.
const dataPrevistaCreditoPontos = (dataVencimento) =>
    (dataVencimento ? dayjs(String(dataVencimento).slice(0, 10)) : dayjs())
        .add(DIAS_CREDITO_PONTOS_APOS_VENCIMENTO, 'day');

const PagarFaturaModal = ({ isOpen, onClose, onSave, fatura, transacoes = [], isMobile = false }) => {
    const { temModuloMilhas } = useAuth();
    const [form] = Form.useForm();
    const [contas, setContas] = useState([]);
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [cotacaoBid, setCotacaoBid] = useState(null);
    const [cotacaoErro, setCotacaoErro] = useState('');
    const [portadores, setPortadores] = useState([]);
    const [programas, setProgramas] = useState([]);
    const [upsellMilhasOpen, setUpsellMilhasOpen] = useState(false);
    const [messageApi, contextHolder] = message.useMessage();

    const criarPontosWatch = Form.useWatch('criar_pontos_cartao', form);

    const cartao = fatura?.cartao || null;
    const regraPontos = cartao?.regra_pontos || 'NENHUMA';
    const pontosPorUnidade = cartao?.pontos_por_unidade;
    const mostraPontos = Boolean(fatura && regraPontos && regraPontos !== 'NENHUMA' && Number(pontosPorUnidade) > 0);

    const totalParciais = useMemo(
        () => somarPagamentosParciaisFatura(transacoes),
        [transacoes],
    );
    const valorBasePontos = useMemo(
        () => valorBasePontosFatura(fatura?.valor_total, totalParciais),
        [fatura?.valor_total, totalParciais],
    );

    const estimativa = useMemo(() => {
        if (!mostraPontos) return { pontos: null, detalhe: '' };
        const base = estimarPontosFatura({
            valorFatura: valorBasePontos,
            regraPontos,
            pontosPorUnidade,
            cotacaoBid,
        });
        if (!base.detalhe || totalParciais <= 0) return base;
        const fmt = (n) =>
            Number(n).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
        return {
            ...base,
            detalhe: `${base.detalhe} (base: ${fmt(valorBasePontos)} = restante ${fmt(fatura?.valor_total)} + parciais ${fmt(totalParciais)})`,
        };
    }, [mostraPontos, valorBasePontos, regraPontos, pontosPorUnidade, cotacaoBid, totalParciais, fatura?.valor_total]);

    useEffect(() => {
        if (!isOpen || !fatura) return;

        const carregar = async () => {
            setIsLoading(true);
            setError('');
            setCotacaoBid(null);
            setCotacaoErro('');
            setProgramas([]);
            form.resetFields();

            try {
                const contasData = await getContas();
                setContas(contasData || []);

                const contaPadraoId = fatura.cartao?.conta_associada?.id;
                const contaInicial = contaPadraoId || (contasData.length > 0 ? contasData[0].id : undefined);

                const obsPadrao = observacaoPontosFatura({
                    nomeCartao: fatura.cartao_nome || cartao?.nome,
                    mes: fatura.mes_referencia,
                    ano: fatura.ano_referencia,
                });

                form.setFieldsValue({
                    valor: fatura.valor_total,
                    data_pagamento: dayjs(),
                    conta_id: contaInicial,
                    criar_pontos_cartao: false,
                    pc_observacao: obsPadrao,
                    pc_data_credito: dataPrevistaCreditoPontos(fatura.data_vencimento),
                });

                if (mostraPontos && regraPontos === 'POR_DOLAR') {
                    try {
                        const dataFechamento = fatura.data_fechamento
                            ? String(fatura.data_fechamento).slice(0, 10)
                            : null;
                        const cot = await getCotacaoUsdBrl(dataFechamento);
                        setCotacaoBid(cot?.bid ?? null);
                    } catch (err) {
                        setCotacaoErro(getErrorMessage(err, 'Cotação do dólar indisponível.'));
                    }
                }

                if (mostraPontos && temModuloMilhas) {
                    try {
                        const portadoresRes = await getPortadores();
                        const list = portadoresRes?.data || portadoresRes || [];
                        const portadoresList = Array.isArray(list) ? list : [];
                        setPortadores(portadoresList);
                        form.setFieldsValue({ pc_portador: undefined, pc_programa: undefined });
                        setProgramas([]);
                        if (portadoresList.length === 1) {
                            const unico = portadoresList[0];
                            form.setFieldsValue({ pc_portador: unico.id });
                            const progRes = await getProgramasDoPortador(unico.id);
                            const progs = progRes?.data || progRes || [];
                            const programasList = Array.isArray(progs) ? progs : [];
                            setProgramas(programasList);
                            if (programasList.length === 1) {
                                form.setFieldsValue({ pc_programa: programasList[0].id });
                            }
                        }
                    } catch (err) {
                        messageApi.warning(getErrorMessage(err, 'Não foi possível carregar programas de milhas.'));
                    }
                }
            } catch (err) {
                setError(getErrorMessage(err, 'Não foi possível carregar as contas.'));
            } finally {
                setIsLoading(false);
            }
        };
        carregar();
    }, [isOpen, fatura, form, mostraPontos, regraPontos, temModuloMilhas, cartao?.nome, messageApi]);

    useEffect(() => {
        if (!isOpen || !mostraPontos) return;
        if (estimativa.pontos != null) {
            form.setFieldsValue({ pc_quantidade: estimativa.pontos });
        }
    }, [isOpen, mostraPontos, estimativa.pontos, form]);

    const preencherPontosEstimados = () => {
        const patch = {};
        if (estimativa.pontos != null) {
            patch.pc_quantidade = estimativa.pontos;
        }
        if (!form.getFieldValue('pc_data_credito')) {
            patch.pc_data_credito = dataPrevistaCreditoPontos(fatura?.data_vencimento);
        }
        if (!form.getFieldValue('pc_observacao')) {
            patch.pc_observacao = observacaoPontosFatura({
                nomeCartao: fatura?.cartao_nome || cartao?.nome,
                mes: fatura?.mes_referencia,
                ano: fatura?.ano_referencia,
            });
        }
        if (Object.keys(patch).length) {
            form.setFieldsValue(patch);
        }
    };

    const handlePortadorChange = async (portadorId) => {
        form.setFieldsValue({ pc_programa: undefined });
        setProgramas([]);
        if (!portadorId) return;
        try {
            const progRes = await getProgramasDoPortador(portadorId);
            const progs = progRes?.data || progRes || [];
            const programasList = Array.isArray(progs) ? progs : [];
            setProgramas(programasList);
            if (programasList.length === 1) {
                form.setFieldsValue({ pc_programa: programasList[0].id });
            }
        } catch (err) {
            messageApi.warning(getErrorMessage(err, 'Falha ao carregar programas.'));
        }
    };

    const onFinish = async (values) => {
        setError('');
        setIsLoading(true);

        const dadosPagamento = {
            conta_id: values.conta_id,
            valor: values.valor,
            data_pagamento: values.data_pagamento ? dayjs(values.data_pagamento).format('YYYY-MM-DD') : null,
        };

        try {
            await pagarFatura(fatura.id, dadosPagamento);

            let successMsg = 'Fatura paga com sucesso!';

            if (mostraPontos && values.criar_pontos_cartao && temModuloMilhas) {
                try {
                    await createPontosCartao({
                        programa_portador: values.pc_programa,
                        quantidade_milhas: values.pc_quantidade,
                        data_transacao: values.pc_data_credito
                            ? dayjs(values.pc_data_credito).format('YYYY-MM-DD')
                            : dayjs().format('YYYY-MM-DD'),
                        observacao: ensureObservacaoComFaturaId(values.pc_observacao, fatura.id),
                        status: 'PENDENTE',
                        custo_total_transacao: 0,
                    });
                    successMsg += ' Entrada de pontos pendente criada.';
                } catch (pcErr) {
                    messageApi.warning(
                        getErrorMessage(pcErr, 'Fatura paga, mas falhou ao criar a entrada de pontos.'),
                        8,
                    );
                }
            }

            onSave(true, successMsg);
        } catch (err) {
            const errorMessage = getErrorMessage(err, 'Erro ao processar o pagamento.');
            setError(errorMessage);
        } finally {
            setIsLoading(false);
        }
    };

    const handleCancel = () => { !isLoading && onClose(); };

    const inputSize = isMobile ? 'large' : 'middle';
    const modalWidth = mostraPontos ? 640 : 520;

    const formBody = (
        <>
                {fatura && (
                    <Descriptions bordered column={1} size="small" style={{ marginBottom: 24 }}>
                        <Descriptions.Item label="Cartão">{fatura.cartao_nome}</Descriptions.Item>
                        <Descriptions.Item label="Valor Total">
                            <Text type="danger" strong>
                                {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(fatura.valor_total)}
                            </Text>
                        </Descriptions.Item>
                    </Descriptions>
                )}

                <Form form={form} layout="vertical" onFinish={onFinish} preserve size={inputSize}>
                    {error && <Alert message={error} type="error" showIcon style={{ marginBottom: 24 }} />}

                    <Form.Item name="conta_id" label="Debitar da Conta" rules={[{ required: true, message: 'Selecione uma conta' }]}>
                        <Select placeholder="Selecione uma conta..." loading={isLoading}>
                            {contas.map(conta => (
                                <Option key={conta.id} value={conta.id}>
                                    {`${conta.nome} (${new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(conta.saldo_atual)})`}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Row gutter={16}>
                        <Col span={isMobile ? 24 : 12}>
                            <Form.Item name="valor" label="Valor a Pagar" rules={[{ required: true, message: 'O valor é obrigatório' }]}>
                                <InputNumber
                                    style={{ width: '100%' }}
                                    precision={2}
                                    step={0.01}
                                    inputMode="decimal"
                                    formatter={(value) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0)}
                                    parser={(value) => {
                                        if (!value) return '';
                                        const onlyNumbers = value.replace(/[^\d]/g, '');
                                        if (!onlyNumbers) return '';
                                        const num = parseInt(onlyNumbers, 10);
                                        return num / 100;
                                    }}
                                />
                            </Form.Item>
                        </Col>
                        <Col span={isMobile ? 24 : 12}>
                            <Form.Item name="data_pagamento" label="Data do Pagamento" rules={[{ required: true, message: 'A data é obrigatória' }]}>
                                <DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                    </Row>

                    {mostraPontos && (
                        <>
                            <Divider>Pontos de cartão (estimativa)</Divider>
                            <Alert
                                type="info"
                                showIcon
                                style={{ marginBottom: 12 }}
                                message={
                                    estimativa.pontos != null
                                        ? `Estimativa (~${estimativa.pontos.toLocaleString('pt-BR')} pontos)`
                                        : 'Estimativa indisponível'
                                }
                                description={
                                    <>
                                        <div>{estimativa.detalhe || cotacaoErro || 'Configure a quantidade manualmente se preferir.'}</div>
                                        <Text type="secondary" style={{ fontSize: 12 }}>
                                            {cotacaoErro
                                                ? (fatura?.data_fechamento
                                                    ? `Não foi possível obter a cotação do dólar para o fechamento (${dayjs(String(fatura.data_fechamento).slice(0, 10)).format('DD/MM/YYYY')}). Você pode informar a quantidade manualmente.`
                                                    : 'Não foi possível obter a cotação do dólar. Você pode informar a quantidade manualmente.')
                                                : 'É uma previsão: cada banco usa um dólar diferente para gerar os pontos.'}
                                        </Text>
                                    </>
                                }
                            />
                            <Form.Item name="criar_pontos_cartao" valuePropName="checked" initialValue={false}>
                                <Checkbox
                                    onChange={(e) => {
                                        if (e.target.checked && !temModuloMilhas) {
                                            form.setFieldsValue({ criar_pontos_cartao: false });
                                            setUpsellMilhasOpen(true);
                                            return;
                                        }
                                        if (e.target.checked && temModuloMilhas) {
                                            // Garante pré-preenchimento ao montar/exibir os campos
                                            setTimeout(() => preencherPontosEstimados(), 0);
                                        }
                                    }}
                                >
                                    Criar entrada de pontos pendente
                                </Checkbox>
                            </Form.Item>
                            <div style={{ display: criarPontosWatch && temModuloMilhas ? 'block' : 'none' }}>
                                <Row gutter={16}>
                                    <Col span={12}>
                                        <Form.Item
                                            name="pc_portador"
                                            label="Portador"
                                            rules={criarPontosWatch && temModuloMilhas ? [{ required: true, message: 'Selecione o portador' }] : []}
                                        >
                                            <Select
                                                placeholder="Portador"
                                                onChange={handlePortadorChange}
                                                options={portadores.map((p) => ({ value: p.id, label: p.nome_completo }))}
                                            />
                                        </Form.Item>
                                    </Col>
                                    <Col span={12}>
                                        <Form.Item
                                            name="pc_programa"
                                            label="Programa"
                                            rules={criarPontosWatch && temModuloMilhas ? [{ required: true, message: 'Selecione o programa' }] : []}
                                        >
                                            <Select
                                                placeholder="Programa"
                                                options={programas.map((p) => ({
                                                    value: p.id,
                                                    label: p.programa_nome || p.nome || `#${p.id}`,
                                                }))}
                                            />
                                        </Form.Item>
                                    </Col>
                                </Row>
                                <Row gutter={16}>
                                    <Col span={12}>
                                        <Form.Item
                                            name="pc_quantidade"
                                            label="Quantidade de pontos"
                                            rules={criarPontosWatch && temModuloMilhas ? [{ required: true, message: 'Informe a quantidade' }] : []}
                                            extra={estimativa.pontos != null ? 'Pré-preenchido com a estimativa (editável)' : undefined}
                                        >
                                            <InputNumber style={{ width: '100%' }} min={1} precision={0} />
                                        </Form.Item>
                                    </Col>
                                    <Col span={12}>
                                        <Form.Item
                                            name="pc_data_credito"
                                            label="Data prevista do crédito"
                                            rules={criarPontosWatch && temModuloMilhas ? [{ required: true, message: 'Informe a data' }] : []}
                                        >
                                            <DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} />
                                        </Form.Item>
                                    </Col>
                                </Row>
                                <Form.Item name="pc_observacao" label="Observação">
                                    <Input.TextArea rows={2} />
                                </Form.Item>
                            </div>
                        </>
                    )}
                </Form>
        </>
    );

    const upsell = (
            <ModuloUpsellModal
                open={upsellMilhasOpen}
                onClose={() => setUpsellMilhasOpen(false)}
                moduloFaltante="milhas"
            />
    );

    if (isMobile) {
        return (
            <>
                {contextHolder}
                <MobileLaunchSheet
                    open={isOpen}
                    title="Pagar Fatura"
                    onCancel={handleCancel}
                    onSave={() => form.submit()}
                    saveLabel="Confirmar"
                    saveLoading={isLoading}
                    cancelDisabled={isLoading}
                >
                    {formBody}
                </MobileLaunchSheet>
                {upsell}
            </>
        );
    }

    return (
        <>
            {contextHolder}
            <Modal
                open={isOpen}
                title="Pagar Fatura"
                onCancel={handleCancel}
                destroyOnClose
                width={modalWidth}
                footer={[
                    <Button key="back" onClick={handleCancel} disabled={isLoading}>Cancelar</Button>,
                    <Button key="submit" type="primary" loading={isLoading} onClick={() => form.submit()}>Confirmar Pagamento</Button>,
                ]}
            >
                {formBody}
            </Modal>
            {upsell}
        </>
    );
};

export default PagarFaturaModal;
